import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Container,
  Fade,
  Grow,
  Avatar,
  Divider,
  Card,
  CardContent,
  useTheme,
  styled,
  alpha
} from "@mui/material";
import {
  Person,
  Business,
  Email,
  Phone,
  Work,
  TrendingUp,
  ArrowForward
} from "@mui/icons-material";

import gpbsLogo from "../assets/gpbs-whitelogo.jpg"; // Import the logo

// Styled components for enhanced visual appeal
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.95)}, ${alpha(theme.palette.common.white, 0.85)})`,
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #f68c4b, #ff9a5a, #f68c4b)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 3s ease-in-out infinite',
  },
  '@keyframes shimmer': {
    '0%, 100%': { backgroundPosition: '-200% 0' },
    '50%': { backgroundPosition: '200% 0' },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: `linear-gradient(145deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.6)})`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(246, 140, 75, 0.15)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f68c4b',
        borderWidth: '2px',
      },
    },
    '&.Mui-focused': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(246, 140, 75, 0.2)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f68c4b',
        borderWidth: '2px',
        boxShadow: '0 0 0 3px rgba(246, 140, 75, 0.1)',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#f68c4b',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha('#f68c4b', 0.3),
    borderWidth: '1px',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f68c4b 0%, #ff9a5a 100%)',
  borderRadius: '16px',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 8px 32px rgba(246, 140, 75, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(246, 140, 75, 0.4)',
    background: 'linear-gradient(135deg, #ff9a5a 0%, #f68c4b 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
    transition: 'left 0.6s',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const FloatingBackground = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: -1,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: 'linear-gradient(45deg, rgba(246, 140, 75, 0.1), rgba(255, 154, 90, 0.1))',
    animation: 'float 20s ease-in-out infinite',
  },
  '&::before': {
    width: '300px',
    height: '300px',
    top: '-150px',
    right: '-150px',
    animationDelay: '0s',
  },
  '&::after': {
    width: '400px',
    height: '400px',
    bottom: '-200px',
    left: '-200px',
    animationDelay: '10s',
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-20px) rotate(180deg)' },
  },
});

function CompanyForm() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    department: "",
    companyName: "",
    companyPhone: "",
    companyEmail: "",
    businessType: "",
    developmentDimension: "",
  });

  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [fieldAnimations, setFieldAnimations] = useState({});
  const theme = useTheme();

  useEffect(() => {
    setIsVisible(true);
    // Stagger field animations
    const timer = setTimeout(() => {
      setFieldAnimations({ show: true });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setFormData({
        ...formData,
        companyPhone: value,
      });
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    localStorage.setItem("companyData", JSON.stringify(formData));
    navigate("/business-assessment");
    // alert("Form submitted successfully! (Navigation disabled in demo)");
  };

  const formSections = [
    {
      title: "Personal Information",
      icon: <Person />,
      fields: [
        { name: "firstName", label: "ชื่อ", icon: <Person fontSize="small" /> },
        { name: "lastName", label: "นามสกุล", icon: <Person fontSize="small" /> },
      ]
    },
    {
      title: "Professional Details",
      icon: <Work />,
      fields: [
        { name: "position", label: "ตำแหน่ง", icon: <Work fontSize="small" /> },
        { name: "department", label: "หน่วยงาน", icon: <Business fontSize="small" /> },
      ]
    },
    {
      title: "Company Information",
      icon: <Business />,
      fields: [
        { name: "companyName", label: "บริษัท", icon: <Business fontSize="small" />, fullWidth: true },
        { name: "companyPhone", label: "เบอร์ติดต่อ", icon: <Phone fontSize="small" />, type: "tel", special: "phone" },
        { name: "companyEmail", label: "Email", icon: <Email fontSize="small" />, type: "email" },
        { name: "businessType", label: "ประเภทธุรกิจ", icon: <TrendingUp fontSize="small" />, fullWidth: true },
      ]
    },
    {
      title: "Development Goals",
      icon: <TrendingUp />,
      fields: [
        { 
          name: "developmentDimension", 
          label: "มิติที่ต้องการพัฒนาบริษัท", 
          icon: <TrendingUp fontSize="small" />, 
          multiline: true, 
          rows: 4,
          fullWidth: true 
        },
      ]
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2F3B4C 0%, #1a2332 50%, #0f1419 100%)',
        position: 'relative',
        py: 4,
      }}
    >
      <FloatingBackground />
      
      <Container maxWidth="lg">
        <Fade in={isVisible} timeout={1000}>
          <Box>
            <StyledPaper elevation={0} sx={{ overflow: 'visible' }}>
              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #2F3B4C 0%, #446084 100%)',
                  color: 'white',
                  textAlign: 'center',
                  py: 6,
                  px: 4,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(246, 140, 75, 0.1), transparent)',
                  }}
                />
                
           <img src={gpbsLogo} width="200px"/>
                
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 2,
                    background: 'linear-gradient(45deg, #fff, #f0f0f0)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  Company Information
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                  Tell us about your business journey and aspirations
                </Typography>
              </Box>

              {/* Form Content */}
              <Box sx={{ p: { xs: 3, md: 6 } }}>
                <Box component="div" onSubmit={handleNext}>
                  {formSections.map((section, sectionIndex) => (
                    <Fade
                      key={section.title}
                      in={fieldAnimations.show}
                      timeout={1000}
                      style={{ transitionDelay: `${sectionIndex * 200}ms` }}
                    >
                      <Card
                        sx={{
                          mb: 4,
                          borderRadius: '16px',
                          background: 'linear-gradient(145deg, rgba(255,255,255,0.7), rgba(248,250,252,0.5))',
                          border: '1px solid rgba(246, 140, 75, 0.1)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 30px rgba(246, 140, 75, 0.15)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar
                              sx={{
                                mr: 2,
                                background: 'linear-gradient(135deg, #f68c4b, #ff9a5a)',
                                width: 40,
                                height: 40,
                              }}
                            >
                              {section.icon}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2F3B4C' }}>
                              {section.title}
                            </Typography>
                          </Box>
                          
                          <Grid container spacing={3}>
                            {section.fields.map((field, fieldIndex) => (
                              <Grid 
                                item 
                                xs={12} 
                                sm={field.fullWidth ? 12 : 6} 
                                key={field.name}
                              >
                                <Grow
                                  in={fieldAnimations.show}
                                  timeout={800}
                                  style={{ transitionDelay: `${(sectionIndex * 200) + (fieldIndex * 100)}ms` }}
                                >
                                  <Box>
                                    <StyledTextField
                                      fullWidth
                                      label={field.label}
                                      name={field.name}
                                      value={formData[field.name]}
                                      onChange={field.special === "phone" ? handlePhoneChange : handleChange}
                                      required
                                      variant="outlined"
                                      type={field.type || "text"}
                                      multiline={field.multiline || false}
                                      rows={field.rows || 1}
                                      inputProps={{
                                        minLength: field.special === "phone" ? 9 : undefined,
                                        maxLength: field.special === "phone" ? 10 : undefined,
                                      }}
                                      InputProps={{
                                        startAdornment: field.icon && (
                                          <Box sx={{ mr: 1, color: '#f68c4b', display: 'flex', alignItems: 'center' }}>
                                            {field.icon}
                                          </Box>
                                        ),
                                      }}
                                    />
                                  </Box>
                                </Grow>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Fade>
                  ))}

                  {/* Submit Button */}
                  <Fade in={fieldAnimations.show} timeout={1000} style={{ transitionDelay: '1000ms' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                      <GradientButton
                        variant="contained"
                        size="large"
                        onClick={handleNext}
                        endIcon={<ArrowForward />}
                      >
                        Next Step
                      </GradientButton>
                    </Box>
                  </Fade>
                </Box>
              </Box>
            </StyledPaper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}

export default CompanyForm;